import axios from "axios";
import { API_URL } from "../config";
import { Rnd } from "react-rnd";
import React, { useState, useRef, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  ArrowLeft,
  Check,
  FileText,
  PenLine,
  ShieldCheck,
  Image as ImageIcon,
  ChevronLeft,
  ChevronRight,
  ZoomIn,
  ZoomOut,
  RotateCcw,
  Lock,
  User,
  BadgeCheck,
  Building2,
  Cpu,
  Clock,
  Link2,
  Database,
  CalendarCheck,
  Key,
  Stamp,
} from "lucide-react";

import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/esm/Page/AnnotationLayer.css";
import "react-pdf/dist/esm/Page/TextLayer.css";

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

/* -------------------------------------------------------------------------- */
/* Constants                                                                   */
/* -------------------------------------------------------------------------- */

const steps = [
  "Upload",
  "Configure Digital Identity",
  "Choose Mode",
  "Advanced Options",
  "Customize Signature",
  "Review & Sign",
];

const PREVIEW_WIDTH = 520;
const FALLBACK_PAGE_HEIGHT = 735;

const DEFAULT_SIGNATURE = {
  xPercent: 0.55,
  yPercent: 0.72,
  width: 200,
  height: 125,
};

// Applied only when the router state value is `undefined` (same semantics as
// destructuring defaults).
const SIGNING_DEFAULTS = {
  selectedTemplateId: 1,
  templateCustomData: {},
  algorithm: "Dilithium3",
  uploadedFiles: [],
  timestampEnabled: true,
  blockchainEnabled: true,
  documentEncryptionEnabled: false,
  encryptionAlgorithm: "AES-256-GCM",
  encryptionPassword: "",
  certificateEmbeddingEnabled: true,
  revocationCheckEnabled: true,
  signaturePolicyEnabled: true,
  documentLockEnabled: true,
  visualSealEnabled: false,
  signerAttributeEnabled: true,
  ipRestrictionEnabled: false,
  signingReasonEnabled: true,
  visibleSignature: true,
  signatureOptions: {},
};

const STEP_CIRCLE_CLASS = {
  done: "bg-green-500 border-green-500 text-white",
  current: "bg-blue-600 border-blue-600 text-white",
  upcoming: "bg-white border-gray-300 text-gray-500",
};

const STEP_LABEL_CLASS = {
  done: "text-green-600",
  current: "text-blue-600",
  upcoming: "text-gray-500",
};

const SUMMARY_VALUE_CLASS = "text-[12px] font-medium text-gray-900";

/* -------------------------------------------------------------------------- */
/* Small pure helpers                                                          */
/* -------------------------------------------------------------------------- */

function isPDF(filename) {
  return filename?.toLowerCase().endsWith(".pdf");
}

function isImage(filename) {
  return /\.(png|jpg|jpeg|gif|bmp|webp)$/i.test(filename || "");
}

function base64ToBlob(b64, type = "application/pdf") {
  const bin = atob(b64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return new Blob([bytes], { type });
}

const orEmpty = (value) => value || "";

function applyDefaults(state, defaults) {
  const result = { ...state };
  Object.entries(defaults).forEach(([key, value]) => {
    if (result[key] === undefined) result[key] = value;
  });
  return result;
}

function getStepStatus(index, currentStep) {
  if (index < currentStep) return "done";
  if (index === currentStep) return "current";
  return "upcoming";
}

function pickField(field, fallback, sources) {
  const found = sources.map((source) => source?.[field]).find(Boolean);
  return found || fallback;
}

function formatFileSize(size) {
  return size ? `${(size / 1024 / 1024).toFixed(2)} MB` : "0.52 MB";
}

function displayAlgorithm(algorithm, signingMode) {
  if (algorithm) return algorithm;
  return signingMode === "hybrid" ? "Dilithium3 + RSA-4096" : "Dilithium3";
}

function statusClass(enabled) {
  return `text-[12px] font-semibold ${enabled ? "text-green-600" : "text-gray-500"}`;
}

function resolveActualFile(selectedFile, uploadedFile) {
  return (
    selectedFile?.file ||
    selectedFile?.originalFile ||
    uploadedFile?.file ||
    uploadedFile?.originalFile ||
    uploadedFile
  );
}

function resolveCertificateData(responseData, activeDigitalId) {
  return (
    responseData?.certificate ||
    responseData?.cert ||
    responseData?.certificate_data ||
    activeDigitalId?.certificate ||
    activeDigitalId?.certificateData ||
    activeDigitalId?.cert ||
    null
  );
}

function buildAfterSignItems(settings) {
  const {
    algorithm,
    blockchainEnabled,
    timestampEnabled,
    visualSealEnabled,
    documentEncryptionEnabled,
    encryptionAlgorithm,
  } = settings;

  return [
    `Document will be digitally signed using Post-Quantum Cryptography (${algorithm}).`,
    "Audit trail will be recorded.",
    blockchainEnabled && "Document hash will be anchored to blockchain.",
    timestampEnabled && "Certificate and timestamp will be embedded.",
    visualSealEnabled && "Visual seal verification will be placed on document.",
    documentEncryptionEnabled && `Document will be encrypted with ${encryptionAlgorithm}.`,
  ].filter(Boolean);
}

/* -------------------------------------------------------------------------- */
/* Signing logic (kept out of the component to keep complexity low)            */
/* -------------------------------------------------------------------------- */

function getSigningError({ agreed, selectedFile, actualFile, activeDigitalId }) {
  if (!agreed) return "Please confirm before signing";
  if (!selectedFile) return "No document selected";
  if (!(actualFile instanceof File)) return "Invalid PDF file";
  if (!activeDigitalId) return "Please select a Digital ID.";

  const idv = activeDigitalId.identityValidation;
  if (idv && new Date(idv.expires_at).getTime() <= Date.now()) {
    return "Your email verification has expired. Please go back to Select Identity and verify again.";
  }
  return null;
}

function buildIdentityFields(ctx) {
  const {
    selectedFile,
    activeDigitalId,
    documentId,
    loggedInUser,
    signingMode,
    mode,
    algorithm,
    selectedTemplateId,
  } = ctx;

  const fields = {
    document_id: orEmpty(documentId),
    user_id: orEmpty(loggedInUser.id),
    digital_id: orEmpty(activeDigitalId.id),
    email: orEmpty(loggedInUser.email),
    sign_type: activeDigitalId.identity_type || "digital_signature",
    signing_mode: signingMode || mode || "PQ",
    algorithm,
    template_id: String(selectedTemplateId),
  };

  // Files from Secure Drive must not be stored on IPFS unlocked by the signing
  // module; the signed copy is locked and stored by Secure Drive afterwards.
  if (selectedFile?.vaultFileId) fields.skip_ipfs_upload = "true";

  const idv = activeDigitalId.identityValidation;
  if (idv) {
    // The backend must check this before signing (see /validations/{id}/verify).
    fields.identity_validation_id = idv.validation_id;
    fields.identity_source = idv.source_type;
  }
  return fields;
}

function buildAdvancedFields(ctx) {
  const fields = {
    timestamp_enabled: String(ctx.timestampEnabled),
    blockchain_enabled: String(ctx.blockchainEnabled),
    encrypt_signed_doc: String(ctx.documentEncryptionEnabled),
    document_encryption_enabled: String(ctx.documentEncryptionEnabled),
  };

  if (ctx.timestampEnabled) {
    fields.timestamp_authority = orEmpty(ctx.timestampAuthority);
    fields.timestamp_hash_algo = orEmpty(ctx.timestampHashAlgo);
  }
  if (ctx.blockchainEnabled) {
    fields.blockchain_type = orEmpty(ctx.blockchainType);
    fields.blockchain_network = orEmpty(ctx.blockchainNetwork);
  }
  if (ctx.documentEncryptionEnabled) {
    fields.encryption_algorithm = ctx.encryptionAlgorithm;
    fields.encryption_password = ctx.encryptionPassword;
  }
  return fields;
}

function buildPolicyFields(ctx) {
  return {
    certificate_embedding_enabled: String(ctx.certificateEmbeddingEnabled),
    certificate_chain_option: orEmpty(ctx.certificateChainOption),
    revocation_check_enabled: String(ctx.revocationCheckEnabled),
    revocation_method: orEmpty(ctx.revocationMethod),
    revocation_fallback: orEmpty(ctx.revocationFallback),
    signature_policy_enabled: String(ctx.signaturePolicyEnabled),
    policy_type: orEmpty(ctx.policyType),
    document_lock_enabled: String(ctx.documentLockEnabled),
    visual_seal_enabled: String(Boolean(ctx.visualSealEnabled)),
    signer_attribute_enabled: String(ctx.signerAttributeEnabled),
    ip_restriction_enabled: String(ctx.ipRestrictionEnabled),
    signing_reason_enabled: String(ctx.signingReasonEnabled),
    visible_signature: String(ctx.visibleSignature),
  };
}

function buildSignatureFields(ctx) {
  const fields = {
    signature_positions: JSON.stringify(ctx.signaturePositions),
    signature_image: orEmpty(ctx.signatureImage),
  };
  if (ctx.visualSealEnabled && ctx.sealImage) {
    fields.seal_image = ctx.sealImage;
  }
  return fields;
}

function buildSignFormData(actualFile, ctx) {
  const formData = new FormData();
  formData.append("pdf", actualFile);

  [
    buildIdentityFields(ctx),
    buildAdvancedFields(ctx),
    buildPolicyFields(ctx),
    buildSignatureFields(ctx),
  ].forEach((fields) => {
    Object.entries(fields).forEach(([key, value]) => formData.append(key, value));
  });

  return formData;
}

// Save the signed PDF to Secure Drive (the backend locks it and stores it on IPFS
// with its own new CID). Non-blocking: the document is already signed.
//  - document came from Secure Drive -> new version of that file
//  - document came from elsewhere     -> new Secure Drive entry
async function saveSignedToVault({ responseData, selectedFile, loggedInUser }) {
  if (!responseData?.signed_pdf) return;

  try {
    const ownerEmail = localStorage.getItem("email") || loggedInUser.email || "";
    const baseName = selectedFile?.name || "document.pdf";
    const signedName = baseName.startsWith("signed_") ? baseName : `signed_${baseName}`;

    const vaultForm = new FormData();
    vaultForm.append("file", base64ToBlob(responseData.signed_pdf), signedName);
    vaultForm.append("owner_email", ownerEmail);
    vaultForm.append("source", "signed");

    if (selectedFile?.vaultFileId) {
      await axios.post(
        `${API_URL}/api/secure-drive/files/${selectedFile.vaultFileId}/versions`,
        vaultForm
      );
      return;
    }

    vaultForm.append("pin", "true");
    vaultForm.append("redundancy", "High (Multiple Nodes)");
    await axios.post(`${API_URL}/api/secure-drive/upload`, vaultForm);
  } catch (vaultErr) {
    console.error("Could not save signed document to Secure Drive:", vaultErr);
  }
}

// Matches the PUT /api/document/status route in document_routes.py.
async function markDocumentSigned(documentId) {
  if (!documentId) return;

  try {
    await axios.put(`${API_URL}/api/document/status`, {
      document_id: documentId,
      status: "signed",
    });
  } catch (statusErr) {
    // The PDF is already signed at this point -- don't block success on this.
    console.error("Could not update document status:", statusErr);
  }
}

function buildSignatureMeta(responseData, ctx) {
  return {
    sign_mode: responseData?.sign_mode,
    algorithm: responseData?.algorithm,
    user_certificate: responseData?.user_certificate || null,
    signed_ipfs_cid: responseData?.signed_ipfs_cid,
    signed_ipfs_url: responseData?.signed_ipfs_url,
    timestamp_token: responseData?.timestamp_token ?? null,
    blockchain_network: ctx.blockchainEnabled ? ctx.blockchainNetwork || null : null,
  };
}

// Signing a workflow request: only AFTER the PDF was signed successfully do we
// mark this recipient's step as done, so Request Status shows "Document Signed".
async function approveWorkflowRequest({ workflowRequestId, responseData, ctx }) {
  if (!workflowRequestId) return;

  try {
    // Send the signed PDF + signing metadata along with the approval so the
    // request's Document Preview and Verification Details can show them.
    const wfForm = new FormData();
    wfForm.append("user_email", (ctx.loggedInUser.email || "").toLowerCase());
    if (responseData?.signed_pdf) {
      wfForm.append(
        "signed_pdf",
        base64ToBlob(responseData.signed_pdf),
        `signed_${ctx.selectedFile?.name || "document.pdf"}`
      );
    }
    wfForm.append("signature_meta", JSON.stringify(buildSignatureMeta(responseData, ctx)));

    await axios.post(
      `${API_URL}/api/workflow/requests/${encodeURIComponent(workflowRequestId)}/approve`,
      wfForm
    );
    sessionStorage.removeItem("workflowRequestId");
  } catch (wfErr) {
    console.error("Could not update request status:", wfErr);
    alert(
      wfErr?.response?.data?.error ||
        "The document was signed, but the request status could not be updated."
    );
  }
}

function buildSuccessState(state, ctx, data) {
  const { visualSealEnabled, documentEncryptionEnabled, loggedInUser, activeDigitalId } = ctx;

  return {
    ...state,
    documentId: ctx.documentId,
    selectedFile: ctx.selectedFile,
    uploadedFiles: ctx.uploadedFiles,
    identity: activeDigitalId,
    selectedDigitalId: activeDigitalId,
    selectedTemplateId: ctx.selectedTemplateId,
    templateCustomData: ctx.templateCustomData,
    signer: {
      id: loggedInUser?.id,
      name: loggedInUser?.name,
      email: loggedInUser?.email,
    },
    mode: ctx.mode || ctx.signingMode,
    signMode: data.sign_mode || ctx.signingMode || ctx.mode,
    algorithm: data.algorithm || ctx.algorithm,
    signedPdf: data.signed_pdf,
    cid: data.cid,
    pageSeals: visualSealEnabled ? data.page_seals : null,
    visualSealEnabled: Boolean(visualSealEnabled),
    timestampEnabled: ctx.timestampEnabled,
    blockchainEnabled: ctx.blockchainEnabled,
    visibleSignature: ctx.visibleSignature,
    documentEncryptionEnabled,
    encryptionAlgorithm: documentEncryptionEnabled ? ctx.encryptionAlgorithm : null,
    certificateEmbeddingEnabled: ctx.certificateEmbeddingEnabled,
    certificateChainOption: ctx.certificateChainOption,
    certificateData: resolveCertificateData(data, activeDigitalId),
    signatureImage: ctx.signatureImage,
    sealImage: visualSealEnabled ? ctx.sealImage : null,
    signaturePositions: ctx.signaturePositions,
    signatureOptions: {
      ...ctx.signatureOptions,
      visualSealEnabled: Boolean(visualSealEnabled),
    },
    signingResult: data,
  };
}

/* -------------------------------------------------------------------------- */
/* Presentational components                                                   */
/* -------------------------------------------------------------------------- */

function StepProgress({ currentStep }) {
  return (
    <div className="flex items-center justify-between mb-2 h-[72px]">
      {steps.map((step, index) => {
        const status = getStepStatus(index, currentStep);
        const isLast = index === steps.length - 1;

        return (
          <div key={step} className="flex-1 flex flex-col items-center relative">
            {!isLast && (
              <div
                className={`absolute top-5 left-1/2 w-full h-[2px] ${
                  status === "done" ? "bg-green-500" : "bg-gray-300"
                }`}
              />
            )}
            <div
              className={`z-10 w-10 h-10 rounded-full flex items-center justify-center text-xs font-semibold border-2 ${STEP_CIRCLE_CLASS[status]}`}
            >
              {status === "done" ? <Check size={12} /> : index + 1}
            </div>
            <p
              className={`mt-1 text-sm font-medium text-center hidden sm:block ${STEP_LABEL_CLASS[status]}`}
            >
              {step}
            </p>
          </div>
        );
      })}
    </div>
  );
}

function ToolbarButton({ onClick, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex items-center justify-center w-7 h-7 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-md border border-slate-200"
    >
      {children}
    </button>
  );
}

function PreviewToolbar({
  fileName,
  currentPage,
  numPages,
  zoom,
  showPlaceButton,
  onPrev,
  onNext,
  onZoomOut,
  onZoomIn,
  onZoomReset,
  onPlaceSignature,
}) {
  return (
    <div className="flex items-center justify-between border-b bg-white px-4 py-2">
      <div className="flex items-center gap-3">
        <p className="text-[14px] font-bold text-gray-800">Document Preview</p>
        <div className="w-px h-4 bg-gray-300" />
        <span className="font-medium text-[12px] text-gray-500 truncate max-w-[200px]">
          {fileName || "Document.pdf"}
        </span>
      </div>
      <div className="flex items-center gap-2">
        <ToolbarButton onClick={onPrev}>
          <ChevronLeft size={14} />
        </ToolbarButton>
        <span className="text-[12px] font-medium text-gray-700 bg-gray-50 border border-gray-200 rounded-md px-2 py-1 min-w-[52px] text-center">
          {currentPage} / {numPages || 1}
        </span>
        <ToolbarButton onClick={onNext}>
          <ChevronRight size={14} />
        </ToolbarButton>
        <div className="w-px h-5 bg-gray-300 mx-1" />
        {showPlaceButton && (
          <button
            type="button"
            onClick={onPlaceSignature}
            className="flex items-center justify-center h-7 px-2 bg-blue-600 hover:bg-blue-700 text-white text-[11px] font-semibold rounded-md"
          >
            Place Signature Here
          </button>
        )}
        <ToolbarButton onClick={onZoomOut}>
          <ZoomOut size={14} />
        </ToolbarButton>
        <span className="text-[12px] font-medium text-gray-700 bg-white border border-slate-200 rounded-md px-2 py-1 min-w-[48px] text-center">
          {Math.round(zoom * 100)}%
        </span>
        <ToolbarButton onClick={onZoomIn}>
          <ZoomIn size={14} />
        </ToolbarButton>
        <ToolbarButton onClick={onZoomReset}>
          <RotateCcw size={13} />
        </ToolbarButton>
      </div>
    </div>
  );
}

function ThumbnailStrip({ file, numPages, currentPage, onSelect }) {
  const pages = Array.from({ length: numPages }, (_, i) => i + 1);

  return (
    <div className="w-[140px] min-w-[140px] border-r border-gray-200 bg-white overflow-y-auto p-3">
      <Document file={file.url}>
        {pages.map((page) => (
          <div
            key={page}
            onClick={() => onSelect(page)}
            className={`mb-3 cursor-pointer rounded-lg border-2 p-1 transition ${
              currentPage === page
                ? "border-blue-500 bg-blue-50"
                : "border-transparent hover:border-blue-200 bg-gray-50"
            }`}
          >
            <Page
              pageNumber={page}
              width={110}
              renderTextLayer={false}
              renderAnnotationLayer={false}
            />
            <p className="text-center text-[12px] py-1 text-gray-500 font-medium">{page}</p>
          </div>
        ))}
      </Document>
    </div>
  );
}

function EmptyPreview({ message, className = "mt-4" }) {
  return (
    <div className="flex flex-col items-center justify-center h-full text-gray-400">
      <FileText size={40} />
      <p className={`${className} text-[12px]`}>{message}</p>
    </div>
  );
}

function PdfLoading() {
  return (
    <div className="flex flex-col items-center justify-center h-64 text-gray-400 gap-2">
      <div className="w-8 h-8 border-2 border-blue-400 border-t-transparent rounded-full animate-spin" />
      <p className="text-[12px]">Loading PDF…</p>
    </div>
  );
}

function SignatureOverlay({ page, zoom, position = {}, signatureImage, getCanvas, onChange }) {
  const canvas = getCanvas(page);
  const canvasWidth = canvas?.clientWidth || PREVIEW_WIDTH;
  const canvasHeight = canvas?.clientHeight || FALLBACK_PAGE_HEIGHT;

  const {
    xPercent = DEFAULT_SIGNATURE.xPercent,
    yPercent = DEFAULT_SIGNATURE.yPercent,
    width = DEFAULT_SIGNATURE.width,
    height = DEFAULT_SIGNATURE.height,
  } = position;

  const handleDragStop = (e, d) => {
    const current = getCanvas(page);
    if (!current) return;
    onChange(page, {
      xPercent: d.x / current.clientWidth,
      yPercent: d.y / current.clientHeight,
    });
  };

  const handleResizeStop = (e, direction, ref, delta, newPosition) => {
    const current = getCanvas(page);
    if (!current) return;
    onChange(page, {
      width: ref.offsetWidth,
      height: ref.offsetHeight,
      xPercent: newPosition.x / current.clientWidth,
      yPercent: newPosition.y / current.clientHeight,
    });
  };

  return (
    <Rnd
      scale={zoom}
      bounds="parent"
      size={{ width, height }}
      position={{ x: xPercent * canvasWidth, y: yPercent * canvasHeight }}
      minWidth={120}
      minHeight={75}
      enableUserSelectHack
      onDragStop={handleDragStop}
      onResizeStop={handleResizeStop}
      style={{
        zIndex: 9999,
        border: "1px dashed #3b82f6",
        borderRadius: "8px",
        background: "rgba(255, 255, 255, 0.95)",
        boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)",
        cursor: "move",
        overflow: "hidden",
      }}
    >
      <img
        src={signatureImage}
        alt="signature"
        draggable={false}
        className="w-full h-full object-contain select-none"
        style={{ pointerEvents: "none" }}
      />
    </Rnd>
  );
}

function PdfContinuousView({
  file,
  numPages,
  zoom,
  signatureImage,
  activeSignaturePage,
  signaturePositions,
  pageRefs,
  onLoadSuccess,
  onSignatureChange,
}) {
  const getCanvas = (page) => pageRefs.current[page]?.querySelector("canvas") || null;
  const pages = Array.from({ length: numPages || 1 }, (_, i) => i + 1);

  return (
    <Document file={file.url} onLoadSuccess={onLoadSuccess} loading={<PdfLoading />}>
      {pages.map((page) => (
        <div
          key={page}
          className="mb-4 shadow-sm"
          style={{
            transform: `scale(${zoom})`,
            transformOrigin: "top center",
            transition: "transform 0.15s ease",
            flexShrink: 0,
            willChange: "transform",
          }}
        >
          <div
            className="relative bg-white"
            ref={(node) => {
              if (node) pageRefs.current[page] = node;
            }}
          >
            <Page pageNumber={page} width={PREVIEW_WIDTH} />

            {signatureImage && page === activeSignaturePage && (
              <SignatureOverlay
                page={page}
                zoom={zoom}
                position={signaturePositions[page]}
                signatureImage={signatureImage}
                getCanvas={getCanvas}
                onChange={onSignatureChange}
              />
            )}
          </div>
        </div>
      ))}
    </Document>
  );
}

function PreviewBody({ file, zoom, pdfProps }) {
  if (!file?.url) return <EmptyPreview message="No document selected" />;
  if (isPDF(file.name)) return <PdfContinuousView file={file} zoom={zoom} {...pdfProps} />;
  if (isImage(file.name)) {
    return (
      <img
        src={file.url}
        alt=""
        style={{ transform: `scale(${zoom})`, transformOrigin: "top center" }}
        className="shadow-md rounded-xl"
      />
    );
  }
  return <EmptyPreview message="Preview unavailable" className="mt-3" />;
}

function DocumentPreviewCard({
  selectedFile,
  currentPage,
  onPageChange,
  signatureImage,
  activeSignaturePage,
  signaturePositions,
  onPlaceSignature,
  onSignatureChange,
}) {
  const [numPages, setNumPages] = useState(null);
  const [zoom, setZoom] = useState(1);
  const mainPreviewRef = useRef(null);
  const pageRefs = useRef({});

  // Keep the continuous preview in sync with currentPage (arrows / thumbnails)
  useEffect(() => {
    const node = pageRefs.current[currentPage];
    if (node) {
      node.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [currentPage]);

  const showThumbnails = isPDF(selectedFile?.name) && numPages;
  const showPlaceButton = Boolean(signatureImage) && currentPage !== activeSignaturePage;

  return (
    <div className="border border-gray-200 rounded-xl overflow-hidden bg-[#f8fafc] shadow-sm">
      <PreviewToolbar
        fileName={selectedFile?.name}
        currentPage={currentPage}
        numPages={numPages}
        zoom={zoom}
        showPlaceButton={showPlaceButton}
        onPrev={() => onPageChange((p) => Math.max(1, p - 1))}
        onNext={() => onPageChange((p) => Math.min(numPages || 1, p + 1))}
        onZoomOut={() => setZoom((z) => Math.max(0.5, parseFloat((z - 0.1).toFixed(1))))}
        onZoomIn={() => setZoom((z) => Math.min(3, parseFloat((z + 0.1).toFixed(1))))}
        onZoomReset={() => setZoom(1)}
        onPlaceSignature={onPlaceSignature}
      />

      <div className="flex" style={{ height: "480px", overflow: "hidden" }}>
        {showThumbnails && (
          <ThumbnailStrip
            file={selectedFile}
            numPages={numPages}
            currentPage={currentPage}
            onSelect={onPageChange}
          />
        )}

        <div
          ref={mainPreviewRef}
          className="flex-1 overflow-y-auto bg-gray-100 flex flex-col items-center py-6 px-4 gap-4"
        >
          <PreviewBody
            file={selectedFile}
            zoom={zoom}
            pdfProps={{
              numPages,
              signatureImage,
              activeSignaturePage,
              signaturePositions,
              pageRefs,
              onLoadSuccess: ({ numPages: total }) => setNumPages(total),
              onSignatureChange,
            }}
          />
        </div>
      </div>
    </div>
  );
}

function VisibleSignatureCard({ onCustomize }) {
  return (
    <div className="mt-4">
      <div className="border border-gray-200 rounded-xl bg-white shadow-sm px-4 py-3">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-8 w-8 items-center justify-center text-blue-600">
              <Lock size={20} />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <p className="text-[12px] font-bold text-gray-900">
                  Visible Signature{" "}
                  <span className="font-normal text-gray-500">(Appearance on Document)</span>
                </p>
                <span className="rounded bg-green-100 px-2 py-0.5 text-[10px] font-bold text-green-700">
                  Enabled
                </span>
              </div>
              <p className="text-[12px] text-gray-500 mt-0.5">
                Choose how your signature should appear on the document.
              </p>
            </div>
          </div>

          <button
            type="button"
            onClick={onCustomize}
            className="inline-flex h-8 items-center justify-center gap-2 rounded-md border border-blue-200 bg-white px-3 text-[12px] font-semibold text-blue-700 transition hover:bg-blue-50"
          >
            <PenLine size={14} />
            Customize Signature
          </button>
        </div>
      </div>
    </div>
  );
}

function AgreementCheckbox({ agreed, onToggle }) {
  return (
    <div className="mt-4 flex items-center gap-2">
      <input
        type="checkbox"
        id="signing-agreement"
        checked={agreed}
        onChange={onToggle}
        className="w-4 h-4 accent-blue-600 rounded cursor-pointer"
      />
      <label
        htmlFor="signing-agreement"
        className="text-[12px] font-medium text-gray-700 cursor-pointer"
      >
        I have reviewed the document and signing details. I agree to sign this document.
      </label>
    </div>
  );
}

function SummaryRow({ icon: Icon, label, valueClassName = SUMMARY_VALUE_CLASS, children }) {
  return (
    <div className="grid grid-cols-[120px_1fr] items-center gap-2">
      <p className="text-[12px] text-gray-500 flex items-center gap-1.5">
        <Icon size={14} /> {label}
      </p>
      <p className={valueClassName}>{children}</p>
    </div>
  );
}

function StatusRow({ icon, label, enabled }) {
  return (
    <SummaryRow icon={icon} label={label} valueClassName={statusClass(enabled)}>
      {enabled ? "Enabled" : "Disabled"}
    </SummaryRow>
  );
}

function SigningSummary({ settings, selectedFile, loggedInUser }) {
  const {
    selectedDigitalId,
    identity,
    algorithm,
    signingMode,
    timestampEnabled,
    blockchainEnabled,
    visualSealEnabled,
    documentEncryptionEnabled,
    encryptionAlgorithm,
  } = settings;
  const ids = [selectedDigitalId, identity];

  return (
    <div className="border border-gray-200 rounded-xl overflow-hidden bg-[#f8fafc] shadow-sm">
      <div className="flex items-center border-b bg-white px-4 py-2">
        <p className="text-[14px] font-bold text-gray-800">Signing Summary</p>
      </div>

      <div className="p-4 space-y-3 bg-white">
        <SummaryRow icon={FileText} label="Document Name" valueClassName={`${SUMMARY_VALUE_CLASS} break-all`}>
          {selectedFile?.name || "Master_Service_Agreement.pdf"}
        </SummaryRow>
        <SummaryRow icon={ImageIcon} label="File Size">
          {formatFileSize(selectedFile?.size)}
        </SummaryRow>

        <hr className="border-gray-100 my-1" />

        <div className="grid grid-cols-[120px_1fr] items-start gap-2">
          <p className="text-[12px] text-gray-500 mt-0.5 flex items-center gap-1.5">
            <User size={14} /> Signer
          </p>
          <div>
            <p className="text-[12px] font-bold text-gray-900">
              {loggedInUser?.name || "Demo Admin"}
            </p>
            <p className="text-[12px] text-blue-600">
              {loggedInUser?.email || "demo@intrustsign.com"}
            </p>
          </div>
        </div>

        <SummaryRow icon={BadgeCheck} label="Identity Type">
          {pickField("type", "Digital Certificate (PQ)", ids)}
        </SummaryRow>
        <SummaryRow icon={Building2} label="Certificate Issuer">
          {pickField("issuer", "Intrust Quantum CA", ids)}
        </SummaryRow>
        <SummaryRow
          icon={ShieldCheck}
          label="Trust Level"
          valueClassName="text-[12px] font-semibold text-green-600"
        >
          {pickField("algorithm", algorithm || "Post-Quantum Secure", ids)}
        </SummaryRow>
        <SummaryRow icon={Cpu} label="Signing Algorithm">
          {displayAlgorithm(algorithm, signingMode)}
        </SummaryRow>

        <StatusRow icon={Clock} label="Timestamping" enabled={timestampEnabled} />
        <StatusRow icon={Link2} label="Blockchain Anchor" enabled={blockchainEnabled} />
        <StatusRow icon={Stamp} label="Visual Seal" enabled={visualSealEnabled} />

        <SummaryRow
          icon={Key}
          label="Doc Encryption"
          valueClassName={`text-[12px] font-semibold ${
            documentEncryptionEnabled ? "text-violet-700 font-bold" : "text-gray-500"
          }`}
        >
          {documentEncryptionEnabled ? encryptionAlgorithm : "Disabled"}
        </SummaryRow>

        <SummaryRow icon={Database} label="CID (IPFS)" valueClassName={`${SUMMARY_VALUE_CLASS} truncate`}>
          Generated after signing
        </SummaryRow>
        <SummaryRow icon={CalendarCheck} label="Valid Till">
          {pickField("valid_to", "N/A", ids)}
        </SummaryRow>
      </div>
    </div>
  );
}

function TrustScoreCard() {
  return (
    <div className="border border-gray-200 rounded-xl bg-white shadow-sm p-2.5 mt-3 flex items-center justify-between">
      <div>
        <h4 className="text-[11px] font-bold text-gray-900">Overall Trust Score After Signing</h4>
        <div className="flex items-baseline mt-1">
          <span className="text-[18px] font-bold text-green-600">98</span>
          <span className="text-[11px] font-semibold text-gray-600 ml-1">/ 100</span>
        </div>
        <div className="mt-0.5">
          <span className="inline-block bg-green-100 text-green-700 text-[10px] font-bold px-1.5 py-0.5 rounded-md">
            Very Strong
          </span>
        </div>
      </div>
      <div className="relative w-[42px] h-[42px] flex items-center justify-center shrink-0">
        <svg className="w-full h-full transform -rotate-180" viewBox="0 0 36 36">
          <path
            className="text-blue-600"
            strokeWidth="3.5"
            stroke="currentColor"
            fill="none"
            strokeLinecap="round"
            d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
          />
          <path
            className="text-green-600"
            strokeDasharray="80, 100"
            strokeWidth="3.5"
            stroke="currentColor"
            fill="none"
            strokeLinecap="round"
            d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
          />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <ShieldCheck size={16} className="text-blue-600" fill="currentColor" color="white" />
        </div>
      </div>
    </div>
  );
}

function AfterSignCard({ items }) {
  return (
    <div className="bg-[#f8fafc] border border-gray-200 rounded-xl shadow-sm p-2.5 mt-2">
      <h4 className="text-[11px] font-bold text-gray-900 mb-1.5">What happens after you sign?</h4>
      <ul className="space-y-1">
        {items.map((text) => (
          <li key={text} className="flex items-start gap-1.5 text-[11px] text-gray-700">
            <Check size={11} className="text-blue-600 shrink-0 mt-0.5" />
            <span>{text}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function BottomActions({ isSigning, onBack, onSign }) {
  return (
    <div className="flex justify-between items-center mt-6">
      <button
        type="button"
        onClick={onBack}
        className="inline-flex items-center gap-1.5 h-10 px-5 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition text-[12px] font-bold shadow-sm"
      >
        <ArrowLeft size={14} /> Back
      </button>
      <button
        type="button"
        disabled={isSigning}
        onClick={onSign}
        className="inline-flex items-center gap-1.5 h-10 px-6 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded-lg text-[12px] font-bold shadow-sm transition"
      >
        <Lock size={14} /> {isSigning ? "Signing..." : "Sign Document"}
      </button>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Page component                                                              */
/* -------------------------------------------------------------------------- */

export default function ReviewSign() {
  const navigate = useNavigate();
  const location = useLocation();

  const loggedInUser = JSON.parse(localStorage.getItem("user")) || {};
  const state = location.state || {};
  const settings = applyDefaults(state, SIGNING_DEFAULTS);
  const { uploadedFiles, uploadedFile, documentId } = settings;

  const currentStep = 5;

  const selectedFile =
    settings.selectedFile || uploadedFiles?.[0] || uploadedFile || settings.documentData || null;

  // Set by App.js when this document came from a request's Sign Document button
  const workflowRequestId =
    selectedFile?.workflowRequestId ||
    uploadedFiles?.[0]?.workflowRequestId ||
    sessionStorage.getItem("workflowRequestId") ||
    null;

  const [currentPage, setCurrentPage] = useState(1);
  const [agreed, setAgreed] = useState(true);
  const [isSigning, setIsSigning] = useState(false);
  const [signatureImage, setSignatureImage] = useState(state.signatureImage || null);

  // Default placement dimensions sized for standard template cards.
  // NOTE: only ONE page should ever be a key in this object — this is a
  // single movable signature, not a multi-stamp feature. activeSignaturePage
  // tracks which page it currently lives on.
  const [activeSignaturePage, setActiveSignaturePage] = useState(1);
  const [signaturePositions, setSignaturePositions] = useState({ 1: { ...DEFAULT_SIGNATURE } });

  useEffect(() => {
    window.scrollTo(0, 0);
    document.querySelector("main")?.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (state.signatureImage) {
      setSignatureImage(state.signatureImage);
    }
  }, [state.signatureImage]);

  // Moves the signature to whichever page is currently in view, replacing
  // any previous placement outright (never merges old pages in) so the
  // final PDF only ever gets stamped where the user last put it.
  const placeSignatureOnCurrentPage = () => {
    setSignaturePositions((prev) => ({
      [currentPage]: {
        ...DEFAULT_SIGNATURE,
        width: prev[activeSignaturePage]?.width || DEFAULT_SIGNATURE.width,
        height: prev[activeSignaturePage]?.height || DEFAULT_SIGNATURE.height,
      },
    }));
    setActiveSignaturePage(currentPage);
  };

  // Drag / resize: always replaces the map with the single active page.
  const updateSignature = (page, patch) => {
    setSignaturePositions((prev) => ({
      [page]: { ...(prev[page] || {}), ...patch },
    }));
  };

  const handleSign = async () => {
    const actualFile = resolveActualFile(selectedFile, uploadedFile);
    const activeDigitalId = settings.selectedDigitalId || settings.identity;

    const validationError = getSigningError({ agreed, selectedFile, actualFile, activeDigitalId });
    if (validationError) {
      alert(validationError);
      return;
    }

    setIsSigning(true);
    try {
      const ctx = {
        ...settings,
        selectedFile,
        activeDigitalId,
        loggedInUser,
        signatureImage,
        signaturePositions,
      };

      const response = await axios.post(`${API_URL}/sign-pdf`, buildSignFormData(actualFile, ctx), {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const responseData = response.data;

      await saveSignedToVault({ responseData, selectedFile, loggedInUser });
      await markDocumentSigned(documentId);
      await approveWorkflowRequest({ workflowRequestId, responseData, ctx });

      navigate("/success", { state: buildSuccessState(state, ctx, responseData) });
    } catch (error) {
      console.error(error);
      alert(error?.response?.data?.error || "Signing failed");
    } finally {
      setIsSigning(false);
    }
  };

  const handleCustomize = () =>
    navigate("/customize-signature", {
      state: {
        ...location.state,
        selectedFile,
        signatureImage,
        signaturePositions,
      },
    });

  return (
    <div className="w-full bg-white mx-auto text-[#1F2937]">
      <StepProgress currentStep={currentStep} />

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_340px] gap-6 items-start">
        {/* LEFT — DOCUMENT PREVIEW */}
        <div className="min-w-0">
          <DocumentPreviewCard
            selectedFile={selectedFile}
            currentPage={currentPage}
            onPageChange={setCurrentPage}
            signatureImage={signatureImage}
            activeSignaturePage={activeSignaturePage}
            signaturePositions={signaturePositions}
            onPlaceSignature={placeSignatureOnCurrentPage}
            onSignatureChange={updateSignature}
          />
          <VisibleSignatureCard onCustomize={handleCustomize} />
          <AgreementCheckbox agreed={agreed} onToggle={() => setAgreed((value) => !value)} />
        </div>

        {/* RIGHT — SIGNING SUMMARY */}
        <div className="w-full" style={{ position: "sticky", top: 0 }}>
          <SigningSummary
            settings={settings}
            selectedFile={selectedFile}
            loggedInUser={loggedInUser}
          />
          <TrustScoreCard />
          <AfterSignCard items={buildAfterSignItems(settings)} />
        </div>
      </div>

      <BottomActions
        isSigning={isSigning}
        onBack={() => navigate("/customize-signature", { state })}
        onSign={handleSign}
      />
    </div>
  );
}